import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserRound } from 'lucide-react';
import api from '../../services/api';
import socketService from '../../services/socket';
import './Notifications.css';

interface Notification {
  id: string;
  type: string;
  actor: {
    username: string;
    profile_picture_url?: string;
  };
  target_id?: string;
  target_type?: string;
  comment_id?: string;
  message: string;
  is_read: boolean;
  created_at: string;
  post?: {
    id: string;
    content: string;
    author_id: string;
    books?: {
      id: string;
      title: string;
      cover_image_url?: string;
    };
  };
  comment?: {
    id: string;
    content: string;
    author_id: string;
  };
}

const Notifications: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Always start at top when visiting notifications
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchNotifications = async (pageNum: number = 1, append: boolean = false) => {
    if (loading) return;

    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/notifications?page=${pageNum}&limit=20`);
      const { notifications: newNotifications, pagination } = response.data;


      if (append) {
        setNotifications(prev => [...prev, ...newNotifications]);
      } else {
        setNotifications(newNotifications);
      }

      setHasMore(pagination.hasMore);
      setPage(pageNum + 1);
    } catch (error) {
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      // Emit event to update navbar unread count
      window.dispatchEvent(new CustomEvent('notificationsRead'));
    } catch (error) {
      // Handle error silently
    }
  };


  const loadMore = () => {
    if (!loading && hasMore) {
      fetchNotifications(page, true);
    }
  };

  // Infinite scroll handler
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const threshold = 200; // Load more when 200px from bottom for more aggressive loading

    if (scrollHeight - scrollTop - clientHeight < threshold && !loading && hasMore) {
      loadMore();
    }
  };

  useEffect(() => {
    fetchNotifications();

    // Set up socket event listeners for real-time updates
    const handleNewNotification = (notification: any) => {
      // Add new notification to the beginning of the list
      setNotifications(prev => [notification, ...prev]);
    };

    const handleDeletedNotification = (data: { id: string }) => {
      // Remove notification from the list
      setNotifications(prev => prev.filter(n => n.id !== data.id));
    };

    socketService.onNotificationNew(handleNewNotification);
    socketService.onNotificationDeleted(handleDeletedNotification);

    return () => {
      socketService.offNotificationNew(handleNewNotification);
      socketService.offNotificationDeleted(handleDeletedNotification);
    };
  }, []);

  // Auto-load more notifications when current batch finishes loading
  useEffect(() => {
    if (!loading && hasMore && notifications.length > 0) {
      // Check if we need to load more based on container height
      const container = document.querySelector('.notifications-list');
      if (container) {
        const { scrollHeight, clientHeight } = container;
        // If content doesn't fill the container, load more
        if (scrollHeight <= clientHeight + 100) {
          loadMore();
        }
      }
    }
  }, [loading, hasMore, notifications.length]);

  // Mark all notifications as read when component mounts
  useEffect(() => {
    const markAllAsReadOnMount = async () => {
      try {
        await api.put('/notifications/mark-all-read');
        // Update local state to reflect all notifications as read
        setNotifications(prev =>
          prev.map(n => ({ ...n, is_read: true }))
        );
        // Emit event to update navbar unread count
        window.dispatchEvent(new CustomEvent('notificationsRead'));
      } catch (error) {
        // Handle error silently
      }
    };

    // Only mark as read if there are unread notifications
    if (notifications.some(n => !n.is_read)) {
      markAllAsReadOnMount();
    }
  }, [notifications.length]); // Run when notifications are loaded

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks}w ago`;
  };

  const truncateContent = (content: string, maxLength: number = 100) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  // For notifications only: remove <br> tags from comment/reply content
  const stripBrTags = (html: string | undefined | null): string => {
    if (!html) return '';
    return html.replace(/<br\s*\/?>(\n)?/gi, ' ');
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read if not already read
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    // If a specific comment is involved (comment or reply or mention), prefer deep-link with highlight
    if (notification.comment_id && notification.target_id) {
      navigate(`/post/${notification.target_id}`, { state: { highlightCommentId: notification.comment_id } });
      return;
    }

    // Otherwise fallback to post-level navigation
    if (notification.target_id && notification.target_type === 'post') {
      navigate(`/post/${notification.target_id}`);
      return;
    }
  };

  const handleUsernameClick = (e: React.MouseEvent, username: string) => {
    e.stopPropagation(); // Prevent notification click
    navigate(`/profile/${username}`);
  };

  const renderNotificationMessage = (notification: Notification) => {
    const message = notification.message;
    const actorUsername = notification.actor.username;


    // Extract username from message for display (for cases where message has different username than actor)
    let displayUsername = actorUsername;

    // For follow notifications, extract the username from the message
    if (notification.type === 'follow') {
      const match = message.match(/^(\w+)\s+started following you$/);

      if (match) {
        displayUsername = match[1];
      }
    }

    // For all notification types, make the username bold and clickable with @ symbol
    const parts = message.split(displayUsername);

    if (parts.length === 2) {
      return (
        <>
          {parts[0]}
          <strong
            className="notification-username-link"
            onClick={(e) => handleUsernameClick(e, actorUsername)}
          >
            @{displayUsername}
          </strong>
          {parts[1]}
        </>
      );
    }

    // Fallback: return the message as is if username not found
    return notification.message;
  };


  if (error) {
    return (
      <div className="notifications-page-container">
        <div className="notifications-error">
          <h3>Something went wrong</h3>
          <p>{error}</p>
          <button onClick={() => fetchNotifications()} className="notifications-retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="notifications-page-container">
      <div className="notifications-list" onScroll={handleScroll}>
        {notifications.length === 0 && !loading ? (
          <div className="notifications-empty">
            <h3>No notifications yet</h3>
            <p>You'll see notifications here when people comment on your posts, reply to your comments, follow you, or like your posts.</p>
          </div>
        ) : (
          notifications.map(notification => (
            <div
              key={notification.id}
              className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
              onClick={() => handleNotificationClick(notification)}
            >
              <div className="notification-avatar">
                {notification.actor?.profile_picture_url ? (
                  <img
                    src={notification.actor.profile_picture_url}
                    alt={`${notification.actor.username}'s profile`}
                    className="notification-avatar-image"
                  />
                ) : (
                  <UserRound className="notification-avatar-icon" />
                )}
              </div>

              <div className="notification-content">
                <div className="notification-header">
                  <p className="notification-message">
                    {renderNotificationMessage(notification)}
                    {notification.comment && (notification.type === 'comment_reply' || notification.type === 'comment') && (
                      <span className="notification-reply-inline">
                        : <strong>"{truncateContent(stripBrTags(notification.comment.content), 80)}"</strong>
                      </span>
                    )}
                    <span className="notification-time">
                      &nbsp; {formatTime(notification.created_at)}
                    </span>
                  </p>

                </div>


                {/* Show post content for like/comment notifications */}
                {notification.post && (notification.type === 'like' || notification.type === 'comment') && (
                  <div className="notification-post-preview">
                    {notification.post.books?.cover_image_url && (
                      <div className="notification-book-cover">
                        <img
                          src={notification.post.books.cover_image_url}
                          alt={notification.post.books.title}
                          className="notification-book-image"
                        />
                      </div>
                    )}
                    <p className="notification-post-content">
                      {truncateContent(notification.post.content, 80)}
                    </p>
                  </div>
                )}
              </div>

            </div>
          ))
        )}

        {loading && (
          <div className="notifications-loading">
            <div className="notification-skeleton">
              <div className="notification-skeleton-avatar"></div>
              <div className="notification-skeleton-content">
                <div className="notification-skeleton-line"></div>
                <div className="notification-skeleton-line short"></div>
              </div>
            </div>
            <div className="notification-skeleton">
              <div className="notification-skeleton-avatar"></div>
              <div className="notification-skeleton-content">
                <div className="notification-skeleton-line"></div>
                <div className="notification-skeleton-line short"></div>
              </div>
            </div>
            <div className="notification-skeleton">
              <div className="notification-skeleton-avatar"></div>
              <div className="notification-skeleton-content">
                <div className="notification-skeleton-line"></div>
                <div className="notification-skeleton-line short"></div>
              </div>
            </div>
            <div className="notification-skeleton">
              <div className="notification-skeleton-avatar"></div>
              <div className="notification-skeleton-content">
                <div className="notification-skeleton-line"></div>
                <div className="notification-skeleton-line short"></div>
              </div>
            </div>
            <div className="notification-skeleton">
              <div className="notification-skeleton-avatar"></div>
              <div className="notification-skeleton-content">
                <div className="notification-skeleton-line"></div>
                <div className="notification-skeleton-line short"></div>
              </div>
            </div>
            <div className="notification-skeleton">
              <div className="notification-skeleton-avatar"></div>
              <div className="notification-skeleton-content">
                <div className="notification-skeleton-line"></div>
                <div className="notification-skeleton-line short"></div>
              </div>
            </div><div className="notification-skeleton">
              <div className="notification-skeleton-avatar"></div>
              <div className="notification-skeleton-content">
                <div className="notification-skeleton-line"></div>
                <div className="notification-skeleton-line short"></div>
              </div>
            </div>
            <div className="notification-skeleton">
              <div className="notification-skeleton-avatar"></div>
              <div className="notification-skeleton-content">
                <div className="notification-skeleton-line"></div>
                <div className="notification-skeleton-line short"></div>
              </div>
            </div>
            <div className="notification-skeleton">
              <div className="notification-skeleton-avatar"></div>
              <div className="notification-skeleton-content">
                <div className="notification-skeleton-line"></div>
                <div className="notification-skeleton-line short"></div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Notifications;
